import React from 'react'
import dayjs from 'dayjs'
import { LatLngExpression } from 'leaflet'
import { Popup } from 'react-leaflet'
import Box from '@mui/material/Box'
import Divider from '@mui/material/Divider'
import Typography from '@mui/material/Typography'
import PinDropIcon from '@mui/icons-material/PinDrop'
import EnhancedMarker from '@/javascript/presentation/components/enhanced-marker/enhanced-marker'
import ErrorBox from '@/components/shared/boxes/ErrorBox'
import TooltipPopover from '@/components/shared/others/TooltipPopover'
import AvatarWithDotStatus from '@/javascript/presentation/components/avatar-with-dot-status'

const formattedDate = (dateString: string): string => {
  const date = dayjs(dateString)
  const isToday = date.isSame(dayjs(), 'date')
  return isToday ? `hoje às ${date.format('HH[h]mm')}` : date.format('DD/MM/YYYY [às] HH[h]mm')
}

type Props = {
  position: LatLngExpression
  imageUrl?: string
  memberName?: string
  memberEmail: string
  datetime: string
  accuracy?: number
  maxAccuracy?: number
}

const MemberMarker: React.FC<Props> = ({
  position, imageUrl, memberName, memberEmail, datetime, accuracy, maxAccuracy
}) => {
  const recentlyUpdated = dayjs(datetime).isBefore(dayjs().subtract(5, 'minute'))
  const belowPrecision = accuracy != null && maxAccuracy != null && accuracy > maxAccuracy
  const isUnavailable = position.toString() === '0,0'
  return (
    <EnhancedMarker
        position={position}
        icon={
          <AvatarWithDotStatus
            imageUrl={imageUrl}
            fallbackText={memberName}
            offline={recentlyUpdated || isUnavailable}
          />
        }
      >
        <Popup className='marker-popup' maxWidth={380}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Typography variant='h6'>{memberName || '-'}</Typography>
            <Typography variant='body1' color="textSecondary">
              {memberEmail}
            </Typography>
            <Divider sx={{ margin: '8px 0' }} />
            <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
              <Typography variant='h6'>
                Últimas atualizações recebidas
              </Typography>
              <TooltipPopover>
                O celular do usuário precisa estar conectado à internet para enviar atualizações para o painel administrativo.
              </TooltipPopover>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <Typography variant='body1' color="textSecondary">
                <PinDropIcon />{isUnavailable ? 'Localização não encontrada' : position.toString()}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', ml: 4 }}>
                <Typography variant="body2" color="textSecondary">Atualizado {formattedDate(datetime)}</Typography>
                <TooltipPopover>
                  <Box component='span' style={{ whiteSpace: 'pre-line' }}>
                    Se a funcionalidade “Localização em tempo real” estiver ativada, o aplicativo ajusta automaticamente a frequência das atualizações conforme o movimento do técnico (o rastreamento será mais frequente quando identificar o usuário em movimento e menos frequente quando identificar o usuário parado). <br/> <br/>
                    Isso ajuda a manter a localização mais precisa quando importa e, ao mesmo tempo, economiza bateria e dados no dia a dia. Ainda assim, o consumo pode variar dependendo do modelo, desempenho e estado do celular.
                  </Box>
                </TooltipPopover>
              </Box>
            </Box>
            {belowPrecision && (
              <ErrorBox
                errorMessage="A localização coletada está abaixo da precisão mínima configurada na conta."
                severity="info"
              />
            )}
            {isUnavailable && (
              <ErrorBox
                errorMessage="GPS do dispositivo indisponível no momento da busca da localização."
              />
            )}
          </Box>
        </Popup>
    </EnhancedMarker>
  )
}

export default MemberMarker
